import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { submitBid } from '../api/api';
import { toast } from 'sonner';
import { Calendar, DollarSign, User, Mail, Phone } from 'lucide-react';

const BiddingModal = ({ property, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    check_in_date: '',
    check_out_date: '',
    bid_amount: property?.nightly_rate || 0,
    message: ''
  });

  const calculateNights = () => {
    if (formData.check_in_date && formData.check_out_date) {
      const checkIn = new Date(formData.check_in_date);
      const checkOut = new Date(formData.check_out_date);
      const diffTime = Math.abs(checkOut - checkIn);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return 0;
  };

  const nights = calculateNights();
  const totalAmount = formData.bid_amount * nights;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (nights === 0) {
      toast.error('Invalid Dates', {
        description: 'Please select valid check-in and check-out dates.',
      });
      return;
    }

    setLoading(true);

    try {
      const bidData = {
        property_id: property.id,
        property_name: property.name,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        bid_amount: parseFloat(formData.bid_amount),
        check_in_date: formData.check_in_date,
        check_out_date: formData.check_out_date,
        number_of_nights: nights,
        message: formData.message
      };

      await submitBid(bidData);
      
      toast.success('Bid Submitted Successfully!', {
        description: `Your bid of £${formData.bid_amount}/night for ${nights} nights has been submitted. We'll review and get back to you shortly.`,
      });

      // Reset form and close modal
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        check_in_date: '',
        check_out_date: '',
        bid_amount: property?.nightly_rate || 0,
        message: ''
      });
      onClose();

    } catch (error) {
      console.error('Error submitting bid:', error);
      toast.error('Submission Failed', {
        description: 'There was an error submitting your bid. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!property) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold" style={{ color: '#1a1a2e' }}>
            Place Your Bid
          </DialogTitle>
          <p style={{ color: '#636e72' }}>
            Submit your bid for <span className="font-semibold">{property.name}</span>
          </p>
        </DialogHeader>

        <div className="p-6 rounded-xl mb-4" style={{ background: '#f8f9fa' }}>
          <div className="flex items-center justify-between mb-2">
            <span style={{ color: '#636e72' }}>Standard Nightly Rate:</span>
            <span className="text-2xl font-bold" style={{ color: '#d4a574' }}>£{property.nightly_rate}</span>
          </div>
          <p className="text-sm" style={{ color: '#636e72' }}>
            You can bid at or below this rate to secure your booking
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_name" className="flex items-center gap-2 mb-2">
                <User size={16} style={{ color: '#d4a574' }} />
                <span style={{ color: '#2d3436' }}>Full Name *</span>
              </Label>
              <Input
                id="customer_name"
                name="customer_name"
                data-testid="bid-name-input"
                value={formData.customer_name}
                onChange={handleChange}
                required
                placeholder="John Smith"
                className="h-11 rounded-xl border-2 focus:border-[#d4a574]"
              />
            </div>

            <div>
              <Label htmlFor="customer_email" className="flex items-center gap-2 mb-2">
                <Mail size={16} style={{ color: '#d4a574' }} />
                <span style={{ color: '#2d3436' }}>Email *</span>
              </Label>
              <Input
                id="customer_email"
                name="customer_email"
                type="email"
                data-testid="bid-email-input"
                value={formData.customer_email}
                onChange={handleChange}
                required
                placeholder="john@example.com"
                className="h-11 rounded-xl border-2 focus:border-[#d4a574]"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="customer_phone" className="flex items-center gap-2 mb-2">
              <Phone size={16} style={{ color: '#d4a574' }} />
              <span style={{ color: '#2d3436' }}>Phone Number *</span>
            </Label>
            <Input
              id="customer_phone"
              name="customer_phone"
              type="tel"
              data-testid="bid-phone-input"
              value={formData.customer_phone}
              onChange={handleChange}
              required
              placeholder="+44 7700 900000"
              className="h-11 rounded-xl border-2 focus:border-[#d4a574]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="check_in_date" className="flex items-center gap-2 mb-2">
                <Calendar size={16} style={{ color: '#d4a574' }} />
                <span style={{ color: '#2d3436' }}>Check-in Date *</span>
              </Label>
              <Input
                id="check_in_date"
                name="check_in_date"
                type="date"
                data-testid="bid-checkin-input"
                value={formData.check_in_date}
                onChange={handleChange}
                required
                min={new Date().toISOString().split('T')[0]}
                className="h-11 rounded-xl border-2 focus:border-[#d4a574]"
              />
            </div>

            <div>
              <Label htmlFor="check_out_date" className="flex items-center gap-2 mb-2">
                <Calendar size={16} style={{ color: '#d4a574' }} />
                <span style={{ color: '#2d3436' }}>Check-out Date *</span>
              </Label>
              <Input
                id="check_out_date"
                name="check_out_date"
                type="date"
                data-testid="bid-checkout-input"
                value={formData.check_out_date}
                onChange={handleChange}
                required
                min={formData.check_in_date || new Date().toISOString().split('T')[0]}
                className="h-11 rounded-xl border-2 focus:border-[#d4a574]"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="bid_amount" className="flex items-center gap-2 mb-2">
              <DollarSign size={16} style={{ color: '#d4a574' }} />
              <span style={{ color: '#2d3436' }}>Your Bid (per night) *</span>
            </Label>
            <Input
              id="bid_amount"
              name="bid_amount"
              type="number"
              step="0.01"
              min="1"
              data-testid="bid-amount-input"
              value={formData.bid_amount}
              onChange={handleChange}
              required
              placeholder="85.00"
              className="h-11 rounded-xl border-2 focus:border-[#d4a574]"
            />
            <p className="text-sm mt-1" style={{ color: '#636e72' }}>
              Bid at or below £{property.nightly_rate} per night
            </p>
          </div>

          {nights > 0 && (
            <div className="p-4 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(212,165,116,0.1), rgba(232,201,161,0.1))' }}>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm" style={{ color: '#636e72' }}>Total for {nights} night{nights > 1 ? 's' : ''}</p>
                  <p className="text-3xl font-bold" style={{ color: '#d4a574' }}>£{totalAmount.toFixed(2)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm" style={{ color: '#636e72' }}>Per night</p>
                  <p className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>£{formData.bid_amount}</p>
                </div>
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="message" style={{ color: '#2d3436' }}>Additional Message (Optional)</Label>
            <Textarea
              id="message"
              name="message"
              data-testid="bid-message-input"
              value={formData.message}
              onChange={handleChange}
              placeholder="Any special requests or questions?"
              className="mt-2 rounded-xl border-2 focus:border-[#d4a574]"
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button 
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 h-12 rounded-full"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              data-testid="submit-bid-button"
              disabled={loading || nights === 0}
              className="flex-1 btn-primary-custom h-12 rounded-full font-semibold"
            >
              {loading ? 'Submitting...' : 'Submit Bid'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BiddingModal;
